import { arraySum } from "common/utilities/core/array";
import { ThemeColors } from "core/utilities/theme";
import type { SequenceAlign } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import {
  conservationColor,
  isConservationMandatoryForSelectedColorScheme,
  type SelectedColorScheme,
} from "sequenceAnalyser/utilities/colors";
import {
  type FeatureDefinition,
  type SequenceAnalyserTableView,
  SequenceAnalyserTableViews,
} from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import { residuesStringToArray } from "sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser";

export interface LetterOccurrence {
  count: number;
  percentage: number;
  top: boolean;
  sameAsFirstLineLetter: boolean;
}

type LetterOccurrences = Record<string, LetterOccurrence>;

// Letter occurrences are calculated for each columns to know the ratio of residues
// This is used as an indicator within the tooltip or when hiding residues according to occurrences
// within the colors tab
export type ColumnsLetterOccurrences = Record<number, LetterOccurrences>;

export function calculateLetterOccurrences(sequences: SequenceAlign[]): ColumnsLetterOccurrences {
  const columns: ColumnsLetterOccurrences = {};
  if (sequences.length === 0) return columns;

  const firstLineResidues = residuesStringToArray(sequences[0].residues);

  sequences.forEach((sequence) => {
    const residues = residuesStringToArray(sequence.residues);

    residues.forEach((residue, position) => {
      if (!columns[position]) {
        columns[position] = {};
      }

      if (!columns[position][residue]) {
        columns[position][residue] = {
          sameAsFirstLineLetter: residue === firstLineResidues[position],
          count: 0,
          percentage: 0,
          top: false,
        };
      }

      columns[position][residue].count += 1;
    });
  });

  // Calculate percentage with gaps
  // When calculating top occurrence(s), ignore gaps
  Object.keys(columns).forEach((p) => {
    const position = Number(p);
    const letterOccurrences = columns[position];
    const columnCount = arraySum(Object.values(letterOccurrences).map((c) => c.count));

    const { letters: topOccurrences } = Object.keys(letterOccurrences).reduce<{ letters: string[]; maxCount: number }>(
      (acc, letter) => {
        if (letter !== ".") {
          if (letterOccurrences[letter].count === acc.maxCount) {
            acc.letters.push(letter);
          } else if (letterOccurrences[letter].count > acc.maxCount) {
            acc.maxCount = letterOccurrences[letter].count;
            acc.letters = [letter];
          }
        }
        return acc;
      },
      { letters: [], maxCount: 0 },
    );

    Object.keys(letterOccurrences).forEach((letter) => {
      columns[position][letter].percentage = Math.floor((letterOccurrences[letter].count * 100) / columnCount);
      columns[position][letter].top = topOccurrences.includes(letter);
    });
  });

  return columns;
}

// Round the percentage to nearest (below) 10, and make it an opacity number (0.1 to 1)
// 8% => 0.1
// 25% => 0.3
// 100% => 1
export function colorOpacityForPercentage(percentage: number) {
  return (Math.floor(percentage / 10) * 10) / 100 + 0.1;
}

// Find the right color for a given residue depending on user's selection of features/colors
export function residueColor(
  residue: string,
  residueForView: string,
  residuePosition: number,
  view: SequenceAnalyserTableView,
  colors: SelectedColorScheme | undefined,
  featureDefinitions: FeatureDefinition[],
  columnsLetterOccurrences: ColumnsLetterOccurrences,
) {
  // Coloring the residue
  let color: number[] | undefined;
  let colorOpacity = 1;

  if (colors) {
    const info = colors.infos[residue];

    if (info) {
      // Scheme color exists
      color = info.color;

      if (info.show === true) {
        if (
          colors.hydrophobicity &&
          info.hydrophobicValue !== undefined &&
          (info.hydrophobicValue < colors.hydrophobicity[0] || info.hydrophobicValue > colors.hydrophobicity[1])
        ) {
          // Clear color based on threshold
          color = undefined;
        }

        if (colors.conservation !== undefined) {
          if (columnsLetterOccurrences[residuePosition][residue].percentage < colors.conservation) color = undefined;

          // If selected, we only show top letters for each column
          if (colors.topOccurrenceOnly && !columnsLetterOccurrences[residuePosition][residue].top) {
            color = undefined;
          } else if (residue !== ".") {
            if (isConservationMandatoryForSelectedColorScheme(colors)) {
              color = conservationColor;
              colorOpacity = colorOpacityForPercentage(columnsLetterOccurrences[residuePosition][residue].percentage);
            }

            // If there is conservation value, we hide the color if needed
            if (columnsLetterOccurrences[residuePosition][residue].percentage < colors.conservation) {
              color = undefined;
            }
          }
        }
      } else {
        // Color set to hidden
        color = undefined;
      }
    }

    if (residueForView === "-" && view === SequenceAnalyserTableViews.abbreviatedWithoutColors) {
      color = undefined;
      colorOpacity = 1;
    }
  } else if (featureDefinitions.length > 0) {
    // If there is no scheme color selected, we show the feature color if there is one
    // Priority order: transcript > protein > gene > last feature
    const priorityTypes = ["protein", "transcript", "gene"];

    const selectedFeature =
      priorityTypes.map((type) => featureDefinitions.find((f) => f.type === type)).find((feature) => feature) ||
      featureDefinitions[featureDefinitions.length - 1];

    if (selectedFeature.visible) color = selectedFeature.color;
  }

  if (residueForView === "-" && view === SequenceAnalyserTableViews.abbreviatedWithoutColorsAndFeaturesColors) {
    color = ThemeColors.sequenceAnalyserAbbreviatedResidue;
    colorOpacity = 1;
  }

  return { color, colorOpacity };
}
